<template>
  <v-container fluid class="px-7 py-6">
    <v-row v-if="!emLote">
      <v-col cols="12" md="8">
        <div class="font-weight-bold grey--text text--darken-1 text-overline">Executar tarefa</div>
        <div class="align-center d-flex mt-1">
          <h2 class="font-weight-medium ma-0 text-h6">Definir encaminhamento</h2>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn aria-label="Como executar esta tarefa" class="ml-1 mt-1" color="primary" icon small v-bind="attrs" v-on="on">
                <v-icon>mdi-help-circle-outline</v-icon>
              </v-btn>
            </template>
            <span class="d-inline-block" style="max-width: 340px">
              Selecione o encaminhamento, responsável pelo tratamento, a prioridade e o período de correção do erro.
            </span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>

    <v-form ref="form" class="mt-5" @submit.prevent="executar">
      <v-row>
        <v-col cols="12" md="4">
          <v-select
            v-model="encaminhamentoInicial"
            :disabled="aguardandoValidacao"
            :items="opcoesEncaminhamento"
            :rules="[regraObrigatoria]"
            item-text="text"
            item-value="value"
            label="Encaminhamento"
            outlined
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="form.responsavel_id"
            :items="responsaveis"
            :loading="carregandoResponsaveis"
            :rules="[regraObrigatoria]"
            item-text="nome"
            item-value="id"
            label="Responsável pela correção"
            outlined
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="form.prioridade"
            :items="prioridades"
            :rules="[regraObrigatoria]"
            item-text="text"
            item-value="value"
            label="Prioridade de correção"
            outlined
          />
        </v-col>

        <v-col cols="12" class="py-0">
          <periodo-data
            labelInicio="Data inicial da correção"
            labelFim="Data final da correção"
            :inicio="dataInicioFormatada"
            :fim="dataFinalFormatada"
            :dense="false"
            obrigatorio
            inicio-a-partir-de-hoje
            @update:inicio="form.data_inicio = dataParaIso($event)"
            @update:fim="form.data_final = dataParaIso($event)"
          />
        </v-col>

        <v-col cols="12" class="py-0">
          <observacao-tarefa v-model="form.descricao" />
        </v-col>

        <v-col cols="12" :class="emLote ? 'pr-0' : 'pt-4'">
          <v-alert v-if="erroExecucao" class="mb-4" dense outlined type="error">{{ erroExecucao }}</v-alert>

          <div class="d-flex justify-end mt-8">
            <botao-confirmar-tarefa label="Encaminhar" :disabled="!formularioPreenchido" :loading="executando || processando" />
          </div>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

  <script>
  import GestaoBdgdsErrosTarefasService from '@/services/GestaoBdgdsErrosTarefasService.js';
  import PeriodoData from '@/components/general/inputs/PeriodoData.vue';
  import BotaoConfirmarTarefa from './BotaoConfirmarTarefa.vue';
  import ObservacaoTarefa from './ObservacaoTarefa.vue';

  const encaminhamentos = [
    { text: 'Analisar primeiro', value: 'ANALISAR_PRIMEIRO' },
    { text: 'Corrigir primeiro', value: 'CORRIGIR_PRIMEIRO' },
    { text: 'Não aplicável', value: 'NAO_APLICAVEL' }
  ];

  const prioridades = [
    { text: 'Baixa', value: 'BAIXA' },
    { text: 'Média', value: 'MEDIA' },
    { text: 'Alta', value: 'ALTA' },
    { text: 'Urgente', value: 'URGENTE' }
  ];

  export default {
    name: 'EncaminharTarefa',

    components: {
      BotaoConfirmarTarefa,
      ObservacaoTarefa,
      PeriodoData
    },

    props: {
      erro: {
        type: Object,
        required: true
      },
      tarefa: {
        type: Object,
        required: true
      },
      emLote: {
        type: Boolean,
        default: false
      },
      processando: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        prioridades,
        responsaveis: [],
        carregandoResponsaveis: false,
        executando: false,
        erroExecucao: '',
        form: {
          encaminhamento_inicial: null,
          prioridade: null,
          responsavel_id: null,
          data_inicio: null,
          data_final: null,
          descricao: ''
        }
      };
    },

    computed: {
      aguardandoValidacao() {
        return String(this.erro.status_erro || '').toUpperCase() === 'AGUARDANDO_VALIDACAO';
      },

      opcoesEncaminhamento() {
        if (!this.aguardandoValidacao) return encaminhamentos;

        return encaminhamentos.filter((opcao) => opcao.value === 'ANALISAR_PRIMEIRO');
      },

      encaminhamentoInicial: {
        get() {
          if (this.aguardandoValidacao) return 'ANALISAR_PRIMEIRO';

          return this.form.encaminhamento_inicial;
        },
        set(valor) {
          this.form.encaminhamento_inicial = this.aguardandoValidacao ? 'ANALISAR_PRIMEIRO' : valor;
        }
      },

      formularioPreenchido() {
        return (
          !this.carregandoResponsaveis &&
          Boolean(this.encaminhamentoInicial) &&
          Boolean(this.form.prioridade) &&
          Boolean(this.form.responsavel_id) &&
          Boolean(this.form.data_inicio) &&
          Boolean(this.form.data_final)
        );
      },

      dataInicioFormatada() {
        return this.dataParaFormatoBr(this.form.data_inicio);
      },

      dataFinalFormatada() {
        return this.dataParaFormatoBr(this.form.data_final);
      }
    },

    mounted() {
      this.carregarResponsaveis();
    },

    methods: {
      async carregarResponsaveis() {
        this.carregandoResponsaveis = true;

        try {
          const { data } = await GestaoBdgdsErrosTarefasService.listarUsuariosEncaminhamento();

          this.responsaveis = Array.isArray(data.data) ? data.data : [];
        } catch (error) {
          this.erroExecucao = 'Não foi possível carregar os responsáveis disponíveis.';
        } finally {
          this.carregandoResponsaveis = false;
        }
      },

      regraObrigatoria(valor) {
        return !!valor || 'Campo obrigatório.';
      },

      dataParaIso(data) {
        if (!data) return null;

        const [dia, mes, ano] = data.split('/');

        return ano && mes && dia ? `${ano}-${mes}-${dia}` : null;
      },

      dataParaFormatoBr(data) {
        if (!data) return null;

        const [ano, mes, dia] = data.split('-');

        return ano && mes && dia ? `${dia}/${mes}/${ano}` : null;
      },

      async executar() {
        this.erroExecucao = '';

        if (!this.$refs.form.validate() || this.processando) return;

        if (this.emLote) {
          this.$emit('confirmar', {
            encaminhamento_inicial: this.encaminhamentoInicial,
            prioridade: this.form.prioridade,
            responsavel_id: this.form.responsavel_id,
            data_inicio: this.form.data_inicio,
            data_final: this.form.data_final,
            descricao: this.form.descricao
          });

          return;
        }

        this.executando = true;

        try {
          await GestaoBdgdsErrosTarefasService.encaminharTarefa(this.erro.id, {
            tarefa_id: this.tarefa.id,
            ...this.form,
            encaminhamento_inicial: this.encaminhamentoInicial
          });

          this.$emit('executada');
        } catch (error) {
          this.erroExecucao = error?.response?.data?.message || 'Não foi possível executar a tarefa.';
        } finally {
          this.executando = false;
        }
      }
    }
  };
</script>
